import {
    useDialogPortal,
    useTransactionFees,
    useLockGasEstimation,
    useTransactionExecution,
    useLockPositions,
} from "@/hooks";
import { lockEventEmitter, LOCK_EVENTS } from "@/events";
import { LOCK_TOKEN_DECIMALS, QUERY_KEYS, getGatewayContractAddress, getLockAddress } from "@/config";
import {
    gatewayStartUnwindingTransaction,
    estimateStartUnwindingTransactionGas,
    prepareStartUnwindingTransaction,
} from "@/services";
import { TOKEN_IDS } from "@/types";
import { useTokenApprovalV2 } from "./useTokenApprovalV2";
import { formatUnits, type WalletClient } from "viem";
import { mainnet } from "viem/chains";
import { convertWeekStringToDurationNumber } from "@/utils";
import { useMemo } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useAccount } from "wagmi";

interface WithdrawLockTransactionProps {
    lockDuration: number;
    withdrawLockAmount: number;
}

export function useWithdrawLockTransaction({ lockDuration, withdrawLockAmount }: WithdrawLockTransactionProps) {
    const { closeDialog } = useDialogPortal();
    const { address } = useAccount();
    const { lockedPositions } = useLockPositions();

    // Find the specific position by lock duration
    const position = useMemo(() => {
        return lockedPositions.find((pos) => {
            return convertWeekStringToDurationNumber(pos.lockDuration) === lockDuration;
        });
    }, [lockedPositions, lockDuration]);

    // Calculate the percentage of this position's locked amount
    const percentageOfPosition = useMemo(() => {
        return position ? withdrawLockAmount / Number(position.lockedBalance) : 0;
    }, [position, withdrawLockAmount]);

    const shares = useMemo(() => {
        return position
            ? (BigInt(position.liUSDBalance) * BigInt(withdrawLockAmount * 1e18)) / BigInt(position.iUSDBalance)
            : BigInt(0);
    }, [position, withdrawLockAmount]);

    const approvalAmount = useMemo(() => formatUnits(shares, LOCK_TOKEN_DECIMALS), [shares]);

    const { isApproved, isApproving } = useTokenApprovalV2({
        tokenId: TOKEN_IDS.LIUSD,
        tokenAddress: getLockAddress(mainnet.id, lockDuration),
        amount: approvalAmount,
        spender: getGatewayContractAddress(mainnet.id),
    });

    const {
        gasEstimate,
        isEstimatingGas,
        error: gasError,
    } = useLockGasEstimation({
        estimateGasFn: (walletClient: WalletClient) =>
            estimateStartUnwindingTransactionGas(shares, lockDuration, walletClient),
        prepareTransactionFn: async (walletClient: WalletClient) =>
            prepareStartUnwindingTransaction(shares, lockDuration, walletClient),
        hasApproval: isApproved,
        lockDuration: lockDuration,
    });

    const { gasPriceUSD, slippagePercent } = useTransactionFees({
        estimatedGas: gasEstimate,
    });

    const { invalidateQueries } = useQueryClient();
    const {
        transactionHash,
        isExecuting,
        error: transactionError,
        executeTransaction: executeWithdrawLockTransaction,
    } = useTransactionExecution({
        executeTransactionFn: (walletClient: WalletClient) => {
            return gatewayStartUnwindingTransaction(shares, lockDuration, walletClient);
        },
        onSuccess: (hash: `0x${string}`) => {
            lockEventEmitter.emit(LOCK_EVENTS.WITHDRAW, {
                transactionHash: hash,
                withdrawAmount: withdrawLockAmount,
                lockDuration,
            });
            closeDialog();
            invalidateQueries({
                queryKey: [[QUERY_KEYS.TOKENS.BALANCE.LIST(address)], [QUERY_KEYS.UNWINDING.USER(address)]],
            });
        },
        errorMessage: "Failed to start withdrawal",
    });

    return {
        withdrawLockAmount,
        approvalAmount,
        gasPriceUSD,
        slippagePercent,
        transactionHash,
        executeWithdrawLockTransaction,
        isEstimatingGas,
        error: gasError || transactionError,
        gasEstimate,
        isLoading: isExecuting || isApproving,
        needsApproval: !isApproved,
        percentageOfPosition,
        isExecuting,
    };
}
